import { ApiError } from "@carepass/api-client";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { useAdminNavigation } from "../../app/navigation/useAdminNavigation";
import { useAdminSession, useAdminUiStore } from "../../app/providers";
import { parseAdminEntityId, pathForAdminEntityScreen } from "../../app/routes";
import { failure } from "../workspace/behavior";
import type { Schemas } from "../workspace/api.generated";
import { StateNotice } from "../workspace/shared";
import { NotificationView } from "./components/NotificationView";
import { useNotificationDetail, useNotificationMutation } from "./queries";
import type { NotificationMutation } from "./queries";

function unresolvedRetryId(commands: Readonly<Record<string, { readonly startedAt: string }>>): number | undefined {
  const item = Object.entries(commands)
    .filter(([key]) => key.startsWith("notification-retry:"))
    .sort(([, left], [, right]) => right.startedAt.localeCompare(left.startedAt))[0];
  const parsed = item ? Number(item[0].slice("notification-retry:".length)) : Number.NaN;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function detailState(query: ReturnType<typeof useNotificationDetail>) {
  if (query.isPending) return "loading" as const;
  if (query.error instanceof ApiError && query.error.kind === "forbidden") return "denied" as const;
  if (query.error instanceof ApiError && query.error.kind === "not-found") return "not-found" as const;
  if (query.isError) return "error" as const;
  return "ready" as const;
}

function NotificationDetailPageForId({ notificationId }: { readonly notificationId: number }) {
  const session = useAdminSession();
  const commands = useAdminUiStore((state) => state.unresolvedCommands);
  const markCommandUnresolved = useAdminUiStore((state) => state.markCommandUnresolved);
  const resolveCommand = useAdminUiStore((state) => state.resolveCommand);
  const pendingRetryId = useMemo(() => unresolvedRetryId(commands), [commands]);
  const query = useNotificationDetail(notificationId);
  const mutation = useNotificationMutation();
  const navigation = useAdminNavigation({
    contextKey: "notifications",
    defaultListPath: "/notifications?tab=history&page=0&size=20",
    originLabel: "알림 상세",
  });
  const [notice, setNotice] = useState("");
  const [recoveryError, setRecoveryError] = useState("");
  const authorities = session.authSession?.authorities ?? [];

  const mutate = async (input: NotificationMutation) => {
    if (input.kind !== "retry") throw new Error(`Unsupported notification detail command: ${input.kind}`);
    const key = `notification-retry:${notificationId}`;
    markCommandUnresolved(key, {
      operation: "POST /api/v1/admin/notifications/{notificationId}/retry",
      targetId: String(notificationId),
      startedAt: new Date().toISOString(),
    });
    try {
      return await mutation.mutateAsync(input);
    } catch (cause) {
      if (!failure(cause).reconcile) resolveCommand(key);
      throw cause;
    }
  };

  const refreshAfterRetry = async () => {
    const result = await query.refetch();
    if (result.error) {
      setRecoveryError("재시도 요청은 접수됐지만 최신 상태를 불러오지 못했습니다. 다시 전송하지 말고 상태를 조회해 주세요.");
      throw result.error;
    }
    resolveCommand(`notification-retry:${notificationId}`);
    setRecoveryError("");
    setNotice(`${result.data?.notification.notificationNo ?? `알림 ID ${notificationId}`}의 최신 상태를 확인했습니다.`);
  };

  const memberId = query.data?.notification.memberId;
  const memberHref = memberId && authorities.includes("MEMBER_READ")
    ? pathForAdminEntityScreen("06", memberId)
    : undefined;

  return <NotificationView
    mode="detail"
    tab="history"
    filters={{ deliveryStatus: "", memberId: "", campaignId: "", from: "", to: "" }}
    notificationState="ready"
    selectedId={notificationId}
    detail={query.data}
    detailState={detailState(query)}
    templatesState="ready"
    policiesState="ready"
    campaignsState="ready"
    canManage={authorities.includes("NOTIFICATION_MANAGE")}
    unresolvedRetryId={pendingRetryId === notificationId ? pendingRetryId : undefined}
    notice={notice}
    recoveryError={recoveryError}
    detailHref={(id) => pathForAdminEntityScreen("26", id)}
    backHref={navigation.backPath}
    backLabel={navigation.backLabel}
    onBack={navigation.backToList}
    memberHref={memberHref}
    onOpenMember={memberHref ? () => navigation.openRelated(memberHref, "알림 상세") : undefined}
    onTabChange={() => undefined}
    onSearch={() => undefined}
    onPageChange={() => undefined}
    onSelect={() => undefined}
    onRetryQuery={(scope) => { if (scope === "detail") void query.refetch(); }}
    onMutation={mutate}
    onMutationComplete={async (input, result) => {
      if (input.kind !== "retry") return;
      await refreshAfterRetry();
      const updated = result as Schemas["NotificationView"];
      setNotice(`${updated.notificationNo} 재시도 결과를 확인했습니다.`);
    }}
    onReconcileRetry={refreshAfterRetry}
  />;
}

export function NotificationDetailPage() {
  const notificationId = parseAdminEntityId(useParams().notificationId);
  if (notificationId === null) return <StateNotice state="not-found" />;
  return <NotificationDetailPageForId key={notificationId} notificationId={notificationId} />;
}
